import React, { useState, useEffect } from "react";
import {
  ChevronRight,
  X,
  CheckCircle,
  Bot,
  MessageSquare,
  HelpCircle,
  Rocket,
} from "lucide-react";
import {
  fetchClaw9Accounts,
  fetchBotAgents,
  fetchAccountFAQs,
} from "../api/claw9";

// Step configuration
const STEPS = [
  { id: "signup", label: "Account", labelVi: "Tài khoản", icon: CheckCircle },
  { id: "create_bot", label: "Create Bot", labelVi: "Tạo Bot", icon: Bot },
  { id: "connect_zalo", label: "Connect Zalo", labelVi: "Kết nối Zalo", icon: MessageSquare },
  { id: "add_faqs", label: "Add FAQs", labelVi: "Thêm FAQ", icon: HelpCircle },
  { id: "complete", label: "Go Live", labelVi: "Hoàn thành", icon: Rocket },
];

/**
 * OnboardingProgressBanner
 * Shows at top of dashboard until onboarding is complete
 */
const OnboardingProgressBanner = ({ onContinue, onDismiss, language = "en" }) => {
  const [completedSteps, setCompletedSteps] = useState(["signup"]);
  const [currentStep, setCurrentStep] = useState("create_bot");
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  const texts = {
    en: {
      title: "Complete your setup",
      step: "Step",
      of: "of",
      continue: "Continue Setup",
      dismiss: "Dismiss",
    },
    vi: {
      title: "Hoàn tất thiết lập",
      step: "Bước",
      of: "/",
      continue: "Tiếp tục",
      dismiss: "Ẩn",
    },
  };
  const t = texts[language] || texts.en;

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const [accountsResult, agentsResult] = await Promise.all([
          fetchClaw9Accounts(),
          fetchBotAgents(),
        ]);

        const accounts = accountsResult.accounts || [];
        const agents = agentsResult.agents || [];
        const completed = ["signup"];

        if (agents.length > 0) {
          completed.push("create_bot");
          const bot = agents[0];

          if (bot.status === "active" || bot.zalo_connected) {
            completed.push("connect_zalo");
          }

          // Check FAQs
          if (accounts.length > 0) {
            try {
              const faqsResult = await fetchAccountFAQs(accounts[0].account_id);
              const faqs = faqsResult.data?.faqs || [];
              if (faqs.length >= 3) {
                completed.push("add_faqs");
              }
            } catch (e) {
              // Ignore
            }
          }
        }

        // If all steps complete, mark as complete
        if (completed.length >= 4) {
          completed.push("complete");
        }

        setCompletedSteps(completed);

        // Find current step
        const stepOrder = ["signup", "create_bot", "connect_zalo", "add_faqs", "complete"];
        for (const step of stepOrder) {
          if (!completed.includes(step)) {
            setCurrentStep(step);
            break;
          }
        }
      } catch (err) {
        console.error("[OnboardingBanner] Error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    // Check if previously dismissed
    const dismissed = localStorage.getItem("claw9_onboarding_dismissed");
    if (dismissed === "true") {
      setIsDismissed(true);
      setIsLoading(false);
      return;
    }

    checkStatus();
  }, []);

  const handleDismiss = () => {
    localStorage.setItem("claw9_onboarding_dismissed", "true");
    setIsDismissed(true);
    if (onDismiss) onDismiss();
  };

  // Don't show if loading, dismissed, or complete
  if (isLoading || isDismissed || completedSteps.includes("complete")) {
    return null;
  }

  const currentStepIndex = STEPS.findIndex((s) => s.id === currentStep);
  const percentComplete = Math.round((completedSteps.length / STEPS.length) * 100);
  const currentStepConfig = STEPS.find((s) => s.id === currentStep);

  return (
    <div className="bg-gradient-to-r from-[#ff4d4d]/10 to-orange-500/10 border border-[#ff4d4d]/20 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          {/* Progress circle */}
          <div className="relative w-12 h-12 flex-shrink-0">
            <svg className="w-12 h-12 -rotate-90">
              <circle
                cx="24"
                cy="24"
                r="20"
                fill="none"
                stroke="rgba(255,255,255,0.1)"
                strokeWidth="4"
              />
              <circle
                cx="24"
                cy="24"
                r="20"
                fill="none"
                stroke="#ff4d4d"
                strokeWidth="4"
                strokeDasharray={`${percentComplete * 1.26} 126`}
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
              {percentComplete}%
            </span>
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white">
              {t.title}: {t.step} {currentStepIndex + 1} {t.of} {STEPS.length} -{" "}
              {language === "vi" ? currentStepConfig?.labelVi : currentStepConfig?.label}
            </p>

            {/* Step indicators */}
            <div className="flex items-center gap-1 mt-2">
              {STEPS.map((step, index) => (
                <div
                  key={step.id}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    completedSteps.includes(step.id)
                      ? "bg-emerald-500"
                      : step.id === currentStep
                      ? "bg-[#ff4d4d]"
                      : "bg-white/10"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={onContinue}
            className="px-4 py-2 bg-[#ff4d4d] hover:bg-[#ff3333] text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1"
          >
            {t.continue}
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={handleDismiss}
            className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title={t.dismiss}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingProgressBanner;
